import type { BodyHandle } from 'box3d-web';
import { destroyOwnedBody } from './nativeOwnership';
import { processCharges, processContacts } from './impactPipeline';
import { processCollapseQueue } from './fractureOrchestrator';
import { runDemolitionInvariants } from './telemetry';
import { spawnMonolith, setLiveCells } from './wallSetup';
import {
  clampAllDynamicLaunches,
  computeMaxDynamicY,
  cullOldRubble,
  demolitionSleepSweep,
  rebuildBuffers,
  recountCells,
  recoverStalledProjectiles,
  wakeDemolitionProjectiles,
} from './debrisPool';
import {
  BLAST_VOLLEY_SUB_BUDGET,
  DEMO_FIXED_DT,
  FADE_DURATION,
  MAX_LIVE_CELLS_MUL,
  clampWeaponPower,
} from './demolitionTuning';
import type { DemoTool, DemolitionContactBatch, DemolitionRuntime } from './types';

/** One fixed hosted tick. The scene owner has already stepped and drained Box3D. */
export function advanceDemolitionHostedFixedStep(
  rt: DemolitionRuntime,
  batch: DemolitionContactBatch,
): void {
  rt.stepper.stepOnce(() => {
    wakeDemolitionProjectiles(rt);
    processContacts(rt, batch);
    recoverStalledProjectiles(rt);
    rt.fixedTime += DEMO_FIXED_DT;
  });
}

/** Hosted per-frame maintenance; deliberately does not step or read contact queues. */
export function finalizeDemolitionHostedFrame(
  rt: DemolitionRuntime,
  delta: number,
  stepped: boolean,
): void {
  if (!Number.isFinite(delta) || delta < 0) throw new RangeError('delta must be finite and non-negative');
  if (rt.idsDirty) rebuildBuffers(rt);
  if (rt.bodyIds.length > 0) rt.buffer.readInto(rt.world);
  processCharges(rt);
  processCollapseQueue(rt);
  if (rt.cannonballPool.sweep(rt.stepper.simTime) > 0) rt.idsDirty = true;
  if (rt.idsDirty) rebuildBuffers(rt);
  if (rt.bodyIds.length > 0) {
    if (stepped && rt.prevValid) rt.prevTransforms.set(rt.transforms);
    rt.buffer.readInto(rt.world);
    if (rt.cellBodyIds.length > 0) rt.world.readSleepStates(rt.cellBodyIds, rt.cellSleepStates);
    if (stepped) {
      clampAllDynamicLaunches(rt);
      demolitionSleepSweep(rt);
      computeAwake(rt);
      computeMaxDynamicY(rt);
      cullOldRubble(rt, DEMO_FIXED_DT);
      recountCells(rt);
      runDemolitionInvariants(rt);
      rt.prevValid = true;
    }
  } else {
    rt.awakeCount = 0;
    rt.maxDynamicY = 0;
  }
  if (delta > 0) {
    let fadeDirty = false;
    for (const rec of rt.records.values()) {
      if (rec.state !== 'removed' || rec.fade < 0 || rec.fade >= 1) continue;
      rec.fade = Math.min(1, rec.fade + delta / FADE_DURATION);
      if (rec.fade >= 1 && rec.bodyId) {
        try { destroyOwnedBody(rt, rec.bodyId as BodyHandle); } catch { /* ignore */ }
        rec.bodyId = 0;
        rt.idsDirty = true;
        fadeDirty = true;
      }
    }
    if (fadeDirty) recountCells(rt);
    else rt.fadingChunks = [...rt.records.values()].filter((r) => r.state === 'removed' && r.fade >= 0 && r.fade < 1).length;
  }
  rt.bodyCountTotal = (rt.groundBodyId ? 1 : 0) + (rt.monolithBodyId ? 1 : 0) +
    [...rt.records.values()].filter((r) => r.bodyId).length + rt.cannonballs.length;
}

export function computeAwake(rt: DemolitionRuntime) {
  if (!rt.prevValid) {
    rt.awakeCount = rt.bodyIds.length;
    return;
  }
  let awake = 0;
  const eps = 0.0006;
  for (let i = 0; i < rt.bodyIds.length; i += 1) {
    const o = i * 7;
    const dx = Math.abs(rt.transforms[o] - rt.prevTransforms[o]);
    const dy = Math.abs(rt.transforms[o + 1] - rt.prevTransforms[o + 1]);
    const dz = Math.abs(rt.transforms[o + 2] - rt.prevTransforms[o + 2]);
    if (dx + dy + dz > eps) awake += 1;
  }
  rt.awakeCount = awake;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function resetDemolition(rt: DemolitionRuntime) {
  // Glass: discard impact-time pristine partition so next hit re-generates.
  if (rt.profile.partitionTiming === 'impact') {
    rt.pristineCells = [];
    rt.baseCells = [];
    rt.pristineCellCount = 0;
    rt.baseCellCount = 0;
    rt.maxLiveCells = MAX_LIVE_CELLS_MUL;
    rt.adjacency.clear();
    rt.pristineCellById.clear();
    setLiveCells(rt, []);
    rt.nextCellId = 0;
  }
  rt.pristineDamage.clear();
  // spawnMonolith restores from pristine, cancels charges, clears markers.
  spawnMonolith(rt);
  rt.sleepSampleValid = false;
  rt.sleepSlowStreak.clear();
  rt.nextSleepSweep = 0;
  rt.lastActivitySimTime = 0;
  rt.maxDynamicY = 0;
  rt.fadingChunks = 0;
  rt.subChunks = 0;
  rt.shardParticles = 0;
  rt.minChunkVol = 0;
  rt.maxChunkVol = 0;
  rt.charges = [];
  rt.volleySubBudget = BLAST_VOLLEY_SUB_BUDGET;
  rt.preCrackedCells = 0;
  rt.lastBlast = null;
  rt.lastResolve = null;
  rt.fragmentVolumes = [];
  rt.pendingPulverizedVolume = 0;
  rt.pendingShardVolume = 0;
  rt.pendingChunkVolume = 0;
  rt.lastPulverizedVolume = 0;
  rt.lastShardVolume = 0;
  rt.lastSpawnedChunkVolume = 0;
  rt.sessionPulverizedVolume = 0;
  rt.residueGeneration += 1;
  // Keep clampEngagements / patchEngagements cumulative across resets for DEV diagnostics.
  rebuildBuffers(rt);
}

/** Scene-wide hard reset of the hosted clock. Structure reset leaves sim-time monotonic. */
export function resetDemolitionClock(rt: DemolitionRuntime): void {
  rt.stepper.reset();
  rt.fixedTime = 0;
}

export function setDemoTool(rt: DemolitionRuntime, tool: DemoTool) {
  rt.tool = tool;
}

export function setWeaponPower(rt: DemolitionRuntime, power: number) {
  rt.weaponPower = clampWeaponPower(power);
  return rt.weaponPower;
}
